import React, { useEffect, useRef, useState } from "react";
import { Send, Image as ImageIcon, X, Smile, Paperclip, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import MentionTextarea from "./MentionTextarea";
import { REACTION_EMOJIS } from "./EmojiReactions";

const COMPOSER_EMOJIS = ["😀", "😂", "🥳", "😎", "🤔", "👍", "❤️", "🎉", "🙌", "🔥", "💯", "✨", "🚀", "🚗", "🛠️", "💡"];
const ATTACH_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/mp4,video/webm";

function urlAbs(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
}

/**
 * Skool-style composer (white surface, red CTA).
 *  - White inputs, no dark backgrounds.
 *  - @-mention autocomplete (real names for /internal, aliases for /community).
 *  - Image/file attachments with inline previews.
 *  - Emoji insert picker.
 *
 * Props:
 *   - scope: "community" | "internal"
 *   - onPosted: callback after success
 *   - dealershipLabel: optional context for internal placeholder
 */
export default function FeedComposer({ scope = "community", onPosted, dealershipLabel }) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [courseTag, setCourseTag] = useState("");
  const [courses, setCourses] = useState([]);
  const [attachments, setAttachments] = useState([]); // [{kind, url, filename, size}]
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (expanded && courses.length === 0 && scope === "community") {
      api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
    }
  }, [expanded, courses.length, scope]);

  useEffect(() => {
    const close = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const cancel = () => {
    setExpanded(false);
    setTitle(""); setBody(""); setCourseTag("");
    setAttachments([]);
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const maxNow = 4 - attachments.length;
    const accepted = Array.from(files).slice(0, maxNow);
    if (accepted.length === 0) {
      toast.error("Max 4 attachments");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of accepted) {
        const form = new FormData();
        form.append("file", f);
        const r = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        // Map upload kind {image,doc,video} to attachment kind {image,video,file}
        const kind = r.data.kind === "image" ? "image" : r.data.kind === "video" ? "video" : "file";
        uploaded.push({
          kind,
          url: r.data.url,
          filename: f.name || r.data.filename,
          size: r.data.size,
        });
      }
      setAttachments((arr) => [...arr, ...uploaded]);
      toast.success(`${uploaded.length} attachment${uploaded.length > 1 ? "s" : ""} added`);
    } catch (e) {
      console.error("upload failed", e);
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((arr) => arr.filter((_, i) => i !== idx));
  };

  const insertEmoji = (emoji) => {
    setBody((b) => b + emoji);
    setEmojiOpen(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() && attachments.length === 0) {
      toast.error("Body or attachment required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: title.trim() || (body.trim().slice(0, 60) || "(attachment)"),
        body: body.trim() || " ",
        attachments,
      };
      if (scope === "community") payload.course_tag = courseTag || null;
      const endpoint = scope === "internal" ? "/internal/posts" : "/community/posts";
      await api.post(endpoint, payload);
      toast.success(scope === "internal" ? "Posted to your team" : "Posted to the community");
      cancel();
      onPosted?.();
    } catch (err) {
      console.error("composer post failed", err);
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  const placeholder = scope === "internal"
    ? `Share something with the team${dealershipLabel ? " at " + dealershipLabel : ""}…  Use @ to tag a teammate`
    : "Share something with the community…  Use @ to tag a member";

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.06)] transition-shadow text-left px-5 py-4 text-[15px] text-[#6B7280] flex items-center gap-3"
        data-testid="composer-collapsed"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fc-red to-fc-navy flex-shrink-0" />
        <span className="flex-1">{placeholder.split("  ")[0]}</span>
        <span className="hidden sm:inline text-[11px] tracking-[0.18em] uppercase font-bold text-fc-red">POST</span>
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-2xl border border-fc-red/40 shadow-[0_4px_14px_rgba(0,0,0,0.06)] p-5 space-y-3"
      data-testid="composer-expanded"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.22em] uppercase font-black text-fc-red">CREATE POST</p>
        <button type="button" onClick={cancel} className="text-[#6B7280] hover:text-fc-navy" data-testid="composer-cancel" aria-label="Cancel">
          <X size={16} />
        </button>
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
        maxLength={160}
        className="w-full bg-white text-fc-navy text-[16px] font-bold border-0 px-0 py-1 focus:outline-none placeholder:text-gray-400"
        data-testid="composer-title"
      />

      <MentionTextarea
        value={body}
        onChange={setBody}
        scope={scope}
        placeholder={placeholder}
        rows={5}
        maxLength={8000}
        autoFocus
        className="w-full bg-white text-fc-navy text-[15px] leading-relaxed border-0 px-0 py-1 focus:outline-none resize-none placeholder:text-gray-400"
        data-testid="composer-body"
      />

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2" data-testid="composer-attachments">
          {attachments.map((a, idx) => (
            <div key={a.url + idx} className="relative group rounded-lg overflow-hidden border border-[#E5E7EB] bg-gray-50">
              {a.kind === "image" ? (
                <img src={urlAbs(a.url)} alt={a.filename} className="w-full h-24 object-cover" />
              ) : a.kind === "video" ? (
                <div className="w-full h-24 flex items-center justify-center bg-gray-900 text-white text-xs flex-col p-2">
                  <Paperclip size={14} className="mb-1" />
                  <span className="truncate w-full text-center">{a.filename}</span>
                </div>
              ) : (
                <div className="w-full h-24 flex items-center justify-center bg-gray-50 text-fc-navy text-xs flex-col p-2">
                  <Paperclip size={14} className="mb-1 text-fc-red" />
                  <span className="truncate w-full text-center">{a.filename}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAttachment(idx)}
                className="absolute top-1 right-1 bg-fc-red text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`composer-remove-attachment-${idx}`}
                aria-label="Remove"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1">
          {/* Attach */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || attachments.length >= 4}
            className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-fc-red/10 hover:text-fc-red transition-colors disabled:opacity-40"
            data-testid="composer-attach-btn"
            title="Attach files"
            aria-label="Attach files"
          >
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <ImageIcon size={16} />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={ATTACH_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(e.target.files)}
            data-testid="composer-file-input"
          />

          {/* Emoji */}
          <div className="relative" ref={emojiRef}>
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-fc-red/10 hover:text-fc-red transition-colors"
              data-testid="composer-emoji-btn"
              title="Insert emoji"
              aria-label="Insert emoji"
            >
              <Smile size={16} />
            </button>
            {emojiOpen && (
              <div className="absolute bottom-full left-0 mb-2 bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-2 grid grid-cols-8 gap-1 z-30 w-[280px]" data-testid="composer-emoji-picker">
                {COMPOSER_EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => insertEmoji(e)}
                    className="w-8 h-8 rounded-lg hover:bg-fc-red/10 text-[18px] leading-none flex items-center justify-center transition-colors"
                    data-testid={`composer-emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Course tag (community only) */}
          {scope === "community" && (
            <select
              value={courseTag}
              onChange={(e) => setCourseTag(e.target.value)}
              className="ml-1 bg-white border border-[#E5E7EB] rounded-lg px-2.5 py-1.5 text-xs text-fc-navy focus:outline-none focus:border-fc-red"
              data-testid="composer-course-tag"
            >
              <option value="">Tag a course (optional)</option>
              {courses.map(c => <option key={c.slug} value={c.slug}>{c.title}</option>)}
            </select>
          )}
        </div>

        <button
          type="submit"
          disabled={busy || uploading || (!body.trim() && attachments.length === 0)}
          className="btn-fc-red text-sm disabled:opacity-40"
          data-testid="composer-submit"
        >
          <Send size={14} /> {busy ? "Posting…" : "Post"}
        </button>
      </div>
    </form>
  );
}
